import { CustomLineSeriesOptions } from '../custom-line-series/options';
import { Delegate, ISubscription } from '../helpers/delegate';
import { displayTime } from '../helpers/time';
import {
  CrosshairMode,
  MouseEventParams,
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IRange,
} from 'lightweight-charts';

const TOOLTIP_CONFIG = {
  Z_INDEX: 9999,
  MARGIN: 10,
  MIN_BOUNDARY: 5,
  DECIMAL_PLACES: 2,
} as const;

const CSS_CLASSES = {
  TOOLTIP_CONTAINER: 'tooltip-container',
  TOOLTIP_TITLE: 'tooltip-title',
  TOOLTIP_SEPARATOR: 'tooltip-separator',
  TOOLTIP_ITEM: 'tooltip-item',
  TOOLTIP_DOT: 'tooltip-dot',
} as const;

export interface TooltipPrimitiveOptions {
  readonly fontSize: number;
  readonly lineColor: string;
  readonly lineTextColor: string;
  readonly lineWith: number;
  readonly panelBackgroundColor: string;
  readonly panelBorderColor: string;
  readonly panelPadding: number;
  readonly panelBorderRadius: number;
}

export interface VoteOption {
  readonly visible: boolean;
  readonly label: string;
  readonly color: string;
  readonly value: number;
}

export interface TooltipCrosshairLineData extends TooltipPrimitiveOptions {
  readonly visible: boolean;
  readonly x: number;
  readonly lineText: string;
  readonly voteOptions?: readonly VoteOption[];
}

const defaultOptions: TooltipPrimitiveOptions = {
  fontSize: 14,
  lineWith: 0.5,
  lineColor: 'rgba(19, 21, 23, 0.08)',
  lineTextColor: 'rgba(19, 21, 23, 0.36)',
  panelBackgroundColor: 'rgba(255, 255, 255, 1)',
  panelBorderColor: 'rgba(54, 56, 59, 0.10)',
  panelPadding: 8,
  panelBorderRadius: 12,
} as const;

export class TooltipPanelPrimitive implements ISeriesPrimitive<Time> {
  private readonly _options: TooltipPrimitiveOptions;
  private readonly _activeRange = new Delegate<IRange<number> | null>();

  private _data: TooltipCrosshairLineData | undefined;
  private _attachedParams: SeriesAttachedParameter<Time> | undefined;
  private _tooltipElement: HTMLDivElement | null = null;
  private _chartContainer: HTMLElement | null = null;

  constructor(options?: Partial<TooltipPrimitiveOptions>) {
    this._options = { ...defaultOptions, ...options };
  }

  private _createTooltipElement(): HTMLDivElement {
    const tooltip = document.createElement('div');
    tooltip.className = CSS_CLASSES.TOOLTIP_CONTAINER;
    const styles = {
      position: 'absolute',
      pointerEvents: 'none',
      zIndex: TOOLTIP_CONFIG.Z_INDEX.toString(),
      background: this._options.panelBackgroundColor,
      border: `1px solid ${this._options.panelBorderColor}`,
      borderRadius: `${this._options.panelBorderRadius}px`,
      padding: `${this._options.panelPadding}px`,
      fontSize: `${this._options.fontSize}px`,
      fontFamily: 'Inter, sans-serif',
      color: this._options.lineTextColor,
      boxShadow: '0px 10px 20px 0px rgba(0, 0, 0, 0.10)',
      display: 'none',
      width: '250px',
      whiteSpace: 'nowrap',
    };
    Object.assign(tooltip.style, styles);
    return tooltip;
  }

  private _calculateTooltipPosition(x: number, chartRect: DOMRect): { x: number; y: number } {
    if (!this._tooltipElement) return { x: 0, y: 0 };
    const tooltipRect = this._tooltipElement.getBoundingClientRect();
    const { MARGIN, MIN_BOUNDARY } = TOOLTIP_CONFIG;
    let tooltipX = x + MARGIN;
    if (tooltipX + tooltipRect.width > chartRect.width - MIN_BOUNDARY) {
      tooltipX = x - tooltipRect.width - MARGIN;
    }
    tooltipX = Math.max(MIN_BOUNDARY, Math.min(tooltipX, chartRect.width - tooltipRect.width - MIN_BOUNDARY));
    const tooltipY = chartRect.height / 2 - tooltipRect.height;
    return { x: tooltipX, y: tooltipY };
  }

  private _formatValue(value: number, total: number = 100): string {
    const percentage = total > 0 ? (value / total) * 100 : 0;
    return percentage.toFixed(TOOLTIP_CONFIG.DECIMAL_PLACES);
  }

  private _createTooltipContent(data: TooltipCrosshairLineData): string {
    const { voteOptions, lineText, lineTextColor } = data;
    if (!voteOptions?.length) return '';
    let content = '';
    if (lineText) {
      content += `<div class="${CSS_CLASSES.TOOLTIP_TITLE}" style="font-weight: 500;text-align: left;padding-left:4px;padding-bottom: 8px;">${lineText}</div>`;
      content += `<div class="${CSS_CLASSES.TOOLTIP_SEPARATOR}" style="border-bottom: 1px solid ${this._options.panelBorderColor}; margin-bottom: 8px;"></div>`;
    }
    const itemsContent = voteOptions
      .map(option => {
        const formattedValue = this._formatValue(option.value);
        return `
        <div class="${CSS_CLASSES.TOOLTIP_ITEM}" style="color: ${lineTextColor};font-weight: 500;display: flex; align-items: center; justify-content: space-between;padding: 4px 2px;">
          <div class="${CSS_CLASSES.TOOLTIP_DOT}"
          style="
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background-color: ${option.color};
            margin-right: 8px;
            border: 2px solid rgba(54, 56, 59, 0.1);
          "></div>
          <div style="flex: 1;">
            ${option.label}
          </div>
          <span style="margin-left: 12px;">${formattedValue}%</span>
        </div>
      `;
      })
      .join('');

    return content + itemsContent;
  }

  private _updateTooltipElement(data?: TooltipCrosshairLineData, chartRect?: DOMRect): void {
    if (!this._tooltipElement) return;
    if (!data?.visible || !data?.voteOptions?.length || !chartRect) {
      this._tooltipElement.style.display = 'none';
      return;
    }
    this._tooltipElement.innerHTML = this._createTooltipContent(data);
    const { x, y } = this._calculateTooltipPosition(data.x, chartRect);
    this._tooltipElement.style.left = `${x}px`;
    this._tooltipElement.style.top = `${y}px`;
    this._tooltipElement.style.display = 'block';
  }

  private _extractSeriesData(param: MouseEventParams<Time>): VoteOption[] {
    const { seriesData } = param;
    if (!seriesData?.size) return [];
    return Array.from(seriesData.entries())
      .map(([series, data]) => {
        if (!data || typeof data !== 'object' || !('value' in data)) {
          return null;
        }
        const seriesOptions = series.options() as CustomLineSeriesOptions;
        return {
          visible: seriesOptions.visible,
          label: seriesOptions.label || seriesOptions.title || 'Series',
          color: seriesOptions.color || seriesOptions.lineColor || '#000000',
          value: data.value as number,
        };
      })
      .filter((option): option is VoteOption => option !== null && option.visible);
    // .sort((a, b) => (b.label.toLowerCase() == 'yes' ? 1 : a.label.toLowerCase() === 'yse' ? 1 : -1));
  }

  private readonly _moveHandler = (param: MouseEventParams<Time>): void => {
    if (!this._attachedParams || !param.logical || !param.time || !param.point) {
      this._hideCrosshair();
      return;
    }
    const chart = this._attachedParams.chart;
    const coordinate = chart.timeScale().logicalToCoordinate(param.logical);
    const voteOptions = this._extractSeriesData(param);
    if (coordinate === null || !voteOptions.length) {
      this._hideCrosshair();
      return;
    }

    const timeFormatter = chart.options().localization.timeFormatter ?? displayTime;
    const formattedTime = timeFormatter(param.time);
    this._activeRange.fire({ from: 0, to: param.logical });
    this.setData({
      ...this._options,
      fontSize: chart.options().layout.fontSize,
      visible: true,
      x: coordinate,
      voteOptions,
      lineText: formattedTime,
    });
  };

  private _hideCrosshair(): void {
    this.setData(undefined);
    this._activeRange.fire(null);
  }

  private _setCrosshairMode(): void {
    this._attachedParams?.chart?.applyOptions({
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: { visible: false, labelVisible: false },
        horzLine: { visible: false, labelVisible: false },
      },
    });
  }

  updateAllViews(): void {
    const chartRect = this._chartContainer?.getBoundingClientRect();
    this._updateTooltipElement(this._data, chartRect);
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._attachedParams = param;
    this._setCrosshairMode();
    param.chart.subscribeCrosshairMove(this._moveHandler);
    this._tooltipElement = this._createTooltipElement();
    const chartElement = param.chart.chartElement();
    if (chartElement?.parentElement) {
      this._chartContainer = chartElement.parentElement;
      this._chartContainer.style.position = 'relative';
      this._chartContainer.appendChild(this._tooltipElement);
    }
  }

  detached(): void {
    if (this._tooltipElement?.parentElement) {
      this._tooltipElement.parentElement.removeChild(this._tooltipElement);
    }
    this._attachedParams?.chart?.unsubscribeCrosshairMove(this._moveHandler);
    this._activeRange.destroy();
    this._tooltipElement = null;
    this._chartContainer = null;
    this._attachedParams = undefined;
  }

  activeRange(): ISubscription<IRange<number> | null> {
    return this._activeRange;
  }

  setData(data?: TooltipCrosshairLineData): void {
    this._data = data;
    this.updateAllViews();
    this._attachedParams?.requestUpdate();
  }

  chart() {
    return this._attachedParams?.chart;
  }

  applyOptions(options: Partial<TooltipPrimitiveOptions>): void {
    Object.assign(this._options, options);
    this.updateAllViews();
  }
}
